import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { contaDaDerivada } from '../contaDaDerivada';
import { hojeIso, numero, paraNumero, plural, rotulo, sinal } from '../formato';
import { useSessao } from '../sessao';
import {
  Aviso, Campo, Carregando, Dialogo, ErroDaApi, Rn, Selo, Vazio,
} from '../ui';

/**
 * A campanha de aferição — a folha de papel do laboratório, na tela.
 *
 * ── Como o laboratorista pensa ────────────────────────────────────────
 *
 * «Preciso aferir as massas de todas as cápsulas de umidade. Preciso
 *  aferir as massas de todos os cilindros CBR.» — Humberto, 01/09/2026.
 *
 * Ele não pensa «vou registrar uma verificação interna do objeto
 * CAP-118». Pensa na CATEGORIA inteira, e na pilha dela em cima da
 * bancada. Pela porta que existia — um certificado, depois um objeto de
 * cada vez, escolhido numa lista de duzentos — quarenta cápsulas são
 * quarenta oportunidades de escolher a linha errada. E escolher a linha
 * errada não dá erro nenhum: publica a tara de uma cápsula na outra, e
 * o defeito aparece num ensaio, três meses depois.
 *
 * ── Por que a folha tem largura variável ──────────────────────────────
 *
 * «não estou conseguindo lançar novos valores de massa ou DIMENSÕES
 *  para anéis, moldes, etc.» — Humberto, 01/09/2026.
 *
 * Porque a folha só pesava. E um anel de adensamento não se resolve
 * pesando: ele tem diâmetro, altura, massa — e o VOLUME, que é o divisor
 * de e₀ e, portanto, do laudo inteiro.
 *
 * As colunas saem do catálogo do banco (`categoria_grandeza`, 0123): a
 * cápsula tem uma, o anel tem quatro. Nenhuma delas está escrita aqui —
 * aferir uma grandeza nova é uma linha de tabela, não um deploy.
 *
 * ── A coluna derivada não se digita ───────────────────────────────────
 *
 * O volume aparece na folha, calculado ao vivo do diâmetro e da altura,
 * para a pessoa CONFERIR o que vai ser publicado. Mas ele não é campo, e
 * esta tela não o envia: quem o calcula, na hora de publicar, é o banco
 * (`fn_previa_do_certificado`). Digitar um volume ao lado do diâmetro e
 * da altura é abrir espaço para os três discordarem.
 *
 * ── O anel de cisalhamento é quadrado (onda 260 · RN-224) ─────────────
 *
 * «o sistema está considerando todos os anéis de cisalhamento como
 *  circulares, mas eles são todos quadrados» — Humberto, 03/10/2026.
 *
 * A folha dele é a do certificado 2611: Largura 1, Largura 2, altura e
 * massa; a ÁREA (L₁·L₂) e o VOLUME (L₁·L₂·h) são as colunas calculadas.
 * As duas larguras são medidas com o mesmo instrumento — um campo só. O
 * anel de adensamento continua redondo: diâmetro e π·d²/4·h. As contas
 * moram em `contaDaDerivada.ts`, as mesmas do banco.
 *
 * ── O desvio aparece, e não é julgado ─────────────────────────────────
 *
 * A coluna Δ mostra a diferença para o valor anterior porque o desvio é
 * um fato — e uma cápsula que mudou 2 g está amassada, ou é outra
 * cápsula. Mas a tela NÃO diz «fora»: quanto um objeto pode variar entre
 * duas campanhas é decisão da Testesolo, e escrever aqui um limite que
 * ninguém aprovou seria inventar critério (§25).
 *
 * ── Uma assinatura para todas ─────────────────────────────────────────
 *
 * A folha vira UM certificado em rascunho. Aprovar continua sendo o que
 * publica (`0094`) e continua sendo outra pessoa (§15) — mas é uma
 * assinatura só, porque foi um evento só.
 */

/*
 * ── Onda 208: a folha vira a edição rápida do conjunto ───────────────
 *
 * «Preciso de uma forma de edição rápida de uma categoria, principalmente
 *  para aferições internas (…) em forma de lista. Também dê a opção de
 *  eliminar um determinado item, que por exemplo tenha sido danificado
 *  (cápsula amassou ou foi extraviada). Também crie a opção de adicionar
 *  novas linhas para adicionar novas cápsulas/itens.» — Humberto,
 *  22/09/2026.
 *
 * Três gestos a mais, todos na mesma lista:
 *
 *  · **Baixa** na própria linha — a cápsula amassada sai do parque ali
 *    mesmo, com tipo e motivo (`RN-159`). NÃO é apagar: o laudo que já
 *    usou a tara dela continua dizendo qual era.
 *  · **Nova linha** — o item novo nasce na folha, com o próximo código
 *    da série, e é aferido na mesma gravação (a API cria e afere numa
 *    transação só).
 *  · **Peneira** — a folha mede a abertura média e a maior abertura, e
 *    mostra o critério da NBR NM ISO 3310-1 antes de medir. A peneira
 *    fina demais para o instrumento escolhido fica travada, com o motivo:
 *    a norma pede leitura de max(1 µm; Y/4) — `RN-197`.
 */

interface Categoria {
  categoria: string; categoria_nome: string; familia: string;
  itens: number; sem_massa: number;
  /** Onda 210: o começo do código (CAP-) e se a folha pede identificação. */
  prefixo_codigo?: string | null;
  folha_identificacao?: boolean;
}

interface Instrumento {
  id: string; codigo_patrimonial: string; nome: string;
  categoria?: string;
  fabricante?: string | null; modelo?: string | null;
  numero_serie?: string | null;
  resolucao: number | null; resolucao_unidade: string | null;
  /** Onda 208: a resolução em mm, e até que peneira ela afere (RN-197). */
  resolucao_mm?: string | number | null;
  menor_peneira_mm?: string | number | null;
  capacidade_nominal?: number | null; capacidade_unidade?: string | null;
  casas_decimais?: number | null;
  impedimento: string | null; impedimento_curto: string | null;
  numero_certificado: string | null; data_validade: string | null;
  data_calibracao?: string | null;
}

/** Uma coluna da folha — vem de `fn_grandezas_da_categoria`. */
interface Grandeza {
  grandeza: string;
  ordem: number;
  unidade: string;
  rotulo: string;
  derivada_de: string[] | null;
  formula: string | null;
  obrigatoria: boolean;
}

interface Anterior {
  equipamento_id: string;
  grandeza: string;
  valor_anterior: string | number | null;
  unidade_anterior: string | null;
  anterior_desde: string | null;
  anterior_certificado: string | null;
}

interface Linha {
  equipamento_id: string; codigo_patrimonial: string; nome: string;
  categoria: string; categoria_nome: string;
  numero_serie: string | null; localizacao: string | null; status: string;
  massa_nominal: number | null;
  massa_anterior: number | null; massa_anterior_unidade: string | null;
  massa_anterior_desde: string | null;
  massa_anterior_certificado: string | null;
  impedimento: string | null; impedimento_curto: string | null;
  /** Onda 210: a gravidade do impedimento e até quando vale o que vale. */
  impedimento_ordem?: number | null;
  valido_ate?: string | null;
}

/** A peneira e o que a NBR NM ISO 3310-1 pede dela (0196). */
interface Peneira {
  equipamento_id: string;
  abertura_mm: string | number | null;
  x_mm: string | number | null;
  y_mm: string | number | null;
  origem: string | null;
  aberturas_a_medir: string | null;
}

interface Folha {
  categorias: Categoria[];
  balancas: Instrumento[];
  instrumentos: Instrumento[];
  grandezas: Grandeza[];
  anteriores: Anterior[];
  /** Onda 260: a última leitura aprovada do que não vira constante (L₁, L₂). */
  leiturasAnteriores?: Anterior[];
  proximoCodigo: string | null;
  linhas: Linha[];
  peneiras?: Peneira[];
  sugestaoCodigo?: { prefixo: string; proximo: number | string; largura: number } | null;
}

/** Uma linha nova — o item ainda não existe; nasce ao gravar. */
interface Nova {
  chave: string;
  codigo: string;
  nome: string;
  abertura: string;
}

/** O que a balança escolhida permite digitar. */
function casasDa(b: Instrumento | undefined): number | null {
  return b?.casas_decimais ?? null;
}

/** Quantas casas a folha mostra para uma grandeza. */
const CASAS_PADRAO: Record<string, number> = {
  massa: 2, diametro: 3, altura: 3, volume: 3, area: 3, espessura: 3,
  abertura_media: 3, abertura_maxima: 3,
  /* O anel quadrado (onda 260): L₁ e L₂ como o paquímetro lê, e a área
     em mm² com as duas casas que o certificado 2611 imprime. */
  largura_1: 2, largura_2: 2,
};

/** A área em mm² do anel quadrado: duas casas, como o 2611. */
const CASAS_DA_DERIVADA: Record<string, number> = { area: 2 };

/**
 * Grandezas medidas com o MESMO instrumento da outra (onda 260): as duas
 * larguras do anel quadrado são lidas no mesmo paquímetro, e a folha pede
 * o instrumento uma vez só — «Instrumento — Larguras».
 */
const SEGUE_O_INSTRUMENTO_DE: Record<string, string> = { largura_2: 'largura_1' };
const ROTULO_DO_INSTRUMENTO: Record<string, string> = {
  largura_1: 'Larguras (L₁ e L₂)',
};

/** As duas colunas da peneira são medidas com o MESMO instrumento. */
const DA_PENEIRA = ['abertura_media', 'abertura_maxima'];

/**
 * Quantos itens cabem num certificado — existentes e novos, cada um (onda
 * 288, RN-233). O mesmo teto da API: a campanha de cápsulas da Testesolo
 * passa de 200 peças, e uma campanha é um certificado só.
 */
const LIMITE_DA_FOLHA = 300;

/** Os tipos de baixa (`RN-159`), na ordem em que acontecem na bancada. */
const TIPOS_DE_BAIXA: { codigo: string; nome: string }[] = [
  { codigo: 'inutilizado', nome: 'Danificado / inutilizado' },
  { codigo: 'extraviado', nome: 'Extraviado' },
  { codigo: 'substituido', nome: 'Substituído' },
  { codigo: 'descartado', nome: 'Descartado' },
  { codigo: 'vendido', nome: 'Vendido' },
];

/** RN-197, na tela: a MESMA conta de `fn_peneira_aferivel` (0196). */
function aferivel(aberturaMm: number | null, resolucaoMm: number | null): boolean | null {
  if (aberturaMm === null || resolucaoMm === null || resolucaoMm <= 0) return null;
  const yFormula = ((aberturaMm * 1000) ** 0.98 / 27 + 1.6) / 1000;
  return resolucaoMm <= Math.max(0.001, yFormula / 4) + 1e-7;
}

/** X e Y (mm) para uma abertura que ainda não está no parque (linha nova). */
function toleranciaPelaFormula(w: number): { x: number; y: number } {
  const um = w * 1000;
  return {
    x: ((2 * um ** 0.75) / 3 + 4 * um ** 0.25) / 1000,
    y: (um ** 0.98 / 27 + 1.6) / 1000,
  };
}

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const dataBr = (iso: string | null | undefined): string =>
  (iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : '');

/**
 * O certificado do padrão contra o dia da aferição (RN-201).
 *
 * «o numero do certificado da balança deverá ser automatico, com o ultimo
 *  certificado cadastrado. Se o certificado estiver vencido na plataforma,
 *  é possivel subir o novo certificado depois» — Humberto, 24/09/2026.
 * A folha não bloqueia: diz. Quem bloqueia a APROVAÇÃO é o banco (0199).
 */
function coberturaDo(i: Instrumento | undefined, dia: string):
  { numero: string | null; ate: string | null; cobre: boolean } | null {
  if (!i) return null;
  const ate = i.data_validade ? String(i.data_validade).slice(0, 10) : null;
  const de = i.data_calibracao ? String(i.data_calibracao).slice(0, 10) : null;
  const cobre = !!i.numero_certificado && !!ate && ate >= dia && (!de || de <= dia);
  return { numero: i.numero_certificado, ate, cobre };
}

/**
 * A ordem da coluna Situação: pendência primeiro (a mais grave antes),
 * depois quem ainda não tem valor, depois quem vence antes.
 */
function chaveDaSituacao(l: Linha, temValor: boolean): [number, number, string] {
  if (l.impedimento) return [0, l.impedimento_ordem ?? 99, ''];
  if (!temValor) return [1, 0, ''];
  return [2, 0, l.valido_ate ? String(l.valido_ate).slice(0, 10) : '9999-12-31'];
}

type Ordem = { campo: 'codigo' | 'situacao'; sentido: 1 | -1 };

export function Aferir() {
  const { pode } = useSessao();
  const navegar = useNavigate();
  const [parametros] = useSearchParams();

  const [folha, setFolha] = useState<Folha | null>(null);
  const [categoria, setCategoria] = useState<string>('');
  const [erro, setErro] = useState<unknown>(null);
  const [carregandoLinhas, setCarregandoLinhas] = useState(false);

  const [codigo, setCodigo] = useState('');
  const [balancaId, setBalancaId] = useState('');
  /* Com que instrumento cada grandeza que não é massa foi medida. */
  const [instrumentoDa, setInstrumentoDa] = useState<Record<string, string>>({});
  const [dataAfericao, setDataAfericao] = useState(hojeIso());
  const [procedimento, setProcedimento] = useState('');
  const [proximaAfericao, setProximaAfericao] = useState('');
  const [condicoes, setCondicoes] = useState('');
  const [observacoes, setObservacoes] = useState('');

  /*
   * O que foi digitado, por objeto e por grandeza. Texto, e não número:
   * «51,4» a meio caminho de «51,41» não é 51,4 — é uma digitação em
   * curso. As linhas novas usam a `chave` delas no lugar do id.
   */
  const [valores, setValores] = useState<Record<string, Record<string, string>>>({});
  const [notas, setNotas] = useState<Record<string, string>>({});
  const [novas, setNovas] = useState<Nova[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [erroSalvar, setErroSalvar] = useState<unknown>(null);

  /* A baixa em andamento, e as que já aconteceram nesta folha. */
  const [baixando, setBaixando] = useState<Linha | null>(null);
  const [tipoBaixa, setTipoBaixa] = useState('inutilizado');
  const [motivoBaixa, setMotivoBaixa] = useState('');
  const [erroBaixa, setErroBaixa] = useState<unknown>(null);
  const [enviandoBaixa, setEnviandoBaixa] = useState(false);
  const [baixados, setBaixados] = useState<{ codigo: string; tipo: string }[]>([]);

  const campos = useRef<Record<string, HTMLInputElement | null>>({});
  const seqNova = useRef(0);
  /* A coluna em que o Enter da última linha estava — a linha nova que ele
     abriu recebe o foco na MESMA coluna. */
  const focoNaNova = useRef<string | null>(null);
  /* Onda 210: com prefixo, a linha nova recebe o foco no CÓDIGO — a
     pessoa digita o número gravado, Enter, a massa. */
  const focoNoCodigo = useRef<string | null>(null);
  useEffect(() => {
    const g = focoNaNova.current;
    const ultima = novas[novas.length - 1];
    const alvo = focoNoCodigo.current;
    if (alvo) {
      focoNoCodigo.current = null;
      focoNaNova.current = null;
      const el = campos.current[`${alvo}|codigo`];
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
      return;
    }
    if (g && ultima) {
      focoNaNova.current = null;
      campos.current[`${ultima.chave}|${g}`]?.focus();
    }
  }, [novas]);
  const [ordem, setOrdem] = useState<Ordem>({ campo: 'codigo', sentido: 1 });

  /*
   * Só a ÚLTIMA leitura vale (onda 210). Aberta com `?categoria=`, a tela
   * pede a folha vazia e a da categoria quase juntas; se a vazia chegasse
   * depois, apagava as linhas — a folha abria sem ninguém.
   */
  const leitura = useRef(0);
  const carregar = useCallback((cat: string) => {
    setCarregandoLinhas(true);
    const minha = ++leitura.current;
    api<Folha>(`/verificacoes/lote${cat ? `?categoria=${encodeURIComponent(cat)}` : ''}`)
      .then((f) => {
        if (minha !== leitura.current) return;
        setFolha(f);
        setCodigo((c) => c || f.proximoCodigo || '');
      })
      .catch((e) => { if (minha === leitura.current) setErro(e); })
      .finally(() => { if (minha === leitura.current) setCarregandoLinhas(false); });
  }, []);

  useEffect(() => { carregar(''); }, [carregar]);

  const escolherCategoria = useCallback((c: string) => {
    setCategoria(c);
    setValores({});
    setNotas({});
    setNovas([]);
    setBaixados([]);
    setInstrumentoDa({});
    setErroSalvar(null);
    carregar(c);
  }, [carregar]);

  /*
   * A ficha do equipamento — e, desde a onda 208, o conjunto aberto na
   * tela do parque — mandam para cá com a categoria já escolhida.
   */
  const daUrl = parametros.get('categoria') ?? '';
  useEffect(() => {
    if (daUrl && !categoria) escolherCategoria(daUrl);
  }, [daUrl, categoria, escolherCategoria]);

  const balanca = folha?.balancas.find((b) => b.id === balancaId);
  const casas = casasDa(balanca);
  const certBalanca = coberturaDo(balanca, dataAfericao);

  /* Onda 210: o que a categoria diz da própria folha. */
  const categoriaAtual = folha?.categorias.find((c) => c.categoria === categoria);
  const prefixo = (categoriaAtual?.prefixo_codigo ?? '').toUpperCase();
  const pedeIdentificacao = categoriaAtual?.folha_identificacao !== false;

  /* Quem já tem algum valor vigente — sem ele não está «em dia». */
  const comValor = useMemo(() => new Set((folha?.anteriores ?? [])
    .filter((a) => a.valor_anterior !== null).map((a) => a.equipamento_id)), [folha]);

  /* A folha na ordem que o cabeçalho pediu — e o Enter desce nela. */
  const linhasOrdenadas = useMemo(() => {
    const l = [...(folha?.linhas ?? [])];
    const porCodigo = (a: Linha, b: Linha) => a.codigo_patrimonial
      .localeCompare(b.codigo_patrimonial, 'pt-BR', { numeric: true });
    l.sort((a, b) => {
      if (ordem.campo === 'situacao') {
        const ka = chaveDaSituacao(a, comValor.has(a.equipamento_id));
        const kb = chaveDaSituacao(b, comValor.has(b.equipamento_id));
        for (let k = 0; k < 3; k += 1) {
          if (ka[k] < kb[k]) return -ordem.sentido;
          if (ka[k] > kb[k]) return ordem.sentido;
        }
        return porCodigo(a, b);
      }
      return porCodigo(a, b) * ordem.sentido;
    });
    return l;
  }, [folha, ordem, comValor]);

  const ordenarPor = (campo: Ordem['campo']) => setOrdem((o) => (o.campo === campo
    ? { campo, sentido: o.sentido === 1 ? -1 : 1 } : { campo, sentido: 1 }));

  const colunas = folha?.grandezas ?? [];
  const medidas = colunas.filter((g) => !g.derivada_de);
  const derivadas = colunas.filter((g) => g.derivada_de);
  const soMassa = medidas.length === 1 && medidas[0]?.grandeza === 'massa';
  const ePeneira = categoria === 'peneira'
    || medidas.some((g) => DA_PENEIRA.includes(g.grandeza));

  /* O instrumento da peneira, e o que ele alcança (RN-197). */
  const instrumentoPeneira = folha?.instrumentos
    .find((i) => i.id === instrumentoDa.abertura_media);
  const resolucaoPeneira = num(instrumentoPeneira?.resolucao_mm);

  const peneiraDe = useMemo(() => {
    const m = new Map<string, Peneira>();
    for (const p of folha?.peneiras ?? []) m.set(p.equipamento_id, p);
    return m;
  }, [folha]);

  /*
   * O valor vigente de cada grandeza, por objeto — e, onde não há valor
   * vigente porque a medida não vira constante (as larguras do anel
   * quadrado, onda 260), a última leitura aprovada. A peneira mostra o
   * critério da norma no lugar do anterior, e continua como era.
   */
  const anteriorDe = useMemo(() => {
    const m = new Map<string, Anterior>();
    for (const a of folha?.anteriores ?? []) {
      m.set(`${a.equipamento_id}|${a.grandeza}`, a);
    }
    if (!ePeneira) {
      for (const a of folha?.leiturasAnteriores ?? []) {
        const k = `${a.equipamento_id}|${a.grandeza}`;
        if (!m.has(k)) m.set(k, a);
      }
    }
    return m;
  }, [folha, ePeneira]);

  const digitado = (id: string, g: string) => valores[id]?.[g] ?? '';
  const mudar = (id: string, g: string, v: string) => setValores((x) => ({
    ...x, [id]: { ...(x[id] ?? {}), [g]: v },
  }));

  /* Quantas casas cada coluna aceita: a massa segue a balança. */
  const casasDa_ = (g: Grandeza): number | null =>
    (g.grandeza === 'massa' ? casas : null);

  /** A abertura nominal e as tolerâncias de uma peneira (mm). */
  const normaDaPeneira = (id: string, nova?: Nova) => {
    if (nova) {
      const w = paraNumero(nova.abertura);
      if (w === undefined || w <= 0) return null;
      const t = toleranciaPelaFormula(w);
      return { w, x: t.x, y: t.y, origem: 'fórmulas (1) e (2) — confira na tabela ao gravar', medir: null };
    }
    const p = peneiraDe.get(id);
    const w = num(p?.abertura_mm);
    if (!p || w === null) return null;
    return {
      w, x: num(p.x_mm) ?? 0, y: num(p.y_mm) ?? 0,
      origem: p.origem, medir: p.aberturas_a_medir,
    };
  };

  /** A peneira que o instrumento escolhido não alcança fica travada. */
  const travadaPelaNorma = (id: string, nova?: Nova): boolean => {
    if (!ePeneira) return false;
    const n = normaDaPeneira(id, nova);
    return aferivel(n?.w ?? null, resolucaoPeneira) === false;
  };

  const medidasDe = (id: string) => {
    const meus: { grandeza: string; valor: number }[] = [];
    for (const g of medidas) {
      const v = paraNumero(digitado(id, g.grandeza));
      if (v !== undefined) meus.push({ grandeza: g.grandeza, valor: v });
    }
    return meus;
  };

  /** As linhas com alguma coisa digitada — e só elas vão. */
  const preenchidas = useMemo(() => {
    if (!folha) return [];
    return folha.linhas
      .map((l) => ({ l, meus: medidasDe(l.equipamento_id) }))
      .filter((x) => x.meus.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folha, valores, medidas]);

  /** As linhas novas com código, nome e ao menos uma medida. */
  const novasPreenchidas = useMemo(
    () => novas
      .map((n) => ({ n, meus: medidasDe(n.chave) }))
      .filter((x) => x.meus.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [novas, valores, medidas]);

  /* A linha nova precisa de código e nome — e a peneira, da abertura. */
  const novasIncompletas = novasPreenchidas
    .filter(({ n }) => !n.codigo.trim() || n.codigo.trim().toUpperCase() === prefixo
      || (n.nome.trim().length === 1)
      || (ePeneira && (paraNumero(n.abertura) ?? 0) <= 0))
    .map(({ n }) => n.codigo.trim() || '(sem código)');

  /** O maior desvio do lote, por grandeza. É um FATO — não um veredito. */
  const maiorDesvio = useMemo(() => {
    let pior: { l: Linha; g: Grandeza; d: number } | null = null;
    for (const { l, meus } of preenchidas) {
      for (const m of meus) {
        const a = anteriorDe.get(`${l.equipamento_id}|${m.grandeza}`);
        if (!a || a.valor_anterior === null) continue;
        const g = medidas.find((x) => x.grandeza === m.grandeza);
        if (!g) continue;
        const d = m.valor - Number(a.valor_anterior);
        if (!pior || Math.abs(d) > Math.abs(pior.d)) pior = { l, g, d };
      }
    }
    return pior;
  }, [preenchidas, anteriorDe, medidas]);

  /* Casas a mais o banco recusa (0095). Avisar antes evita perder a
     folha inteira por causa de uma linha. */
  const casasDemais = useMemo(() => {
    if (casas === null) return [];
    const fora: string[] = [];
    const conferir = (rotulo: string, meus: { grandeza: string; valor: number }[]) => {
      const m = meus.find((x) => x.grandeza === 'massa');
      if (m && Number(m.valor.toFixed(casas)) !== m.valor) fora.push(rotulo);
    };
    for (const { l, meus } of preenchidas) conferir(l.codigo_patrimonial, meus);
    for (const { n, meus } of novasPreenchidas) conferir(n.codigo || '(nova)', meus);
    return fora;
  }, [preenchidas, novasPreenchidas, casas]);

  /* Peneiras lançadas que o instrumento não alcança (RN-197). */
  const finasDemais = [
    ...preenchidas.filter(({ l }) => travadaPelaNorma(l.equipamento_id))
      .map(({ l }) => l.codigo_patrimonial),
    ...novasPreenchidas.filter(({ n }) => travadaPelaNorma(n.chave, n))
      .map(({ n }) => n.codigo || '(nova)'),
  ];

  /* Toda grandeza digitada tem de dizer com que instrumento foi medida. */
  const semInstrumento = useMemo(() => {
    const usadas = new Set<string>();
    for (const { meus } of [...preenchidas, ...novasPreenchidas]) {
      for (const m of meus) usadas.add(m.grandeza);
    }
    return [...usadas].filter((g) => (g === 'massa'
      ? balancaId === '' : !instrumentoDa[g]));
  }, [preenchidas, novasPreenchidas, balancaId, instrumentoDa]);

  const podeCriar = pode('equipamento.editar');
  /*
   * Onda 288 — «o botão de gravar a aferição em lote não habilita»
   * (Humberto, 06/10/2026, com 222 cápsulas na folha). O botão desligava
   * calado: faltava o procedimento, e a tela não dizia. Agora tudo o que
   * desliga o botão sem ter aviso próprio vira uma linha de «Falta para
   * gravar» (RN-233). Os tetos são os mesmos da API.
   */
  const faltaParaGravar: string[] = [];
  if (!pode('equipamento.verificar')) {
    faltaParaGravar.push('a permissão de calibrar (equipamento.verificar)');
  }
  if (codigo.trim() === '') faltaParaGravar.push('o nº deste certificado interno');
  if (procedimento.trim().length < 3) {
    faltaParaGravar.push('o procedimento ou a norma (ao menos 3 caracteres)');
  }
  if (preenchidas.length + novasPreenchidas.length === 0) {
    faltaParaGravar.push('ao menos um valor digitado na folha');
  }
  if (novasPreenchidas.length > 0 && !podeCriar) {
    faltaParaGravar.push('a permissão de cadastrar equipamento (equipamento.editar) para os itens novos');
  }
  if (preenchidas.length > LIMITE_DA_FOLHA) {
    faltaParaGravar.push(`no máximo ${LIMITE_DA_FOLHA} itens existentes por certificado (há ${preenchidas.length})`);
  }
  if (novasPreenchidas.length > LIMITE_DA_FOLHA) {
    faltaParaGravar.push(`no máximo ${LIMITE_DA_FOLHA} itens novos por certificado (há ${novasPreenchidas.length})`);
  }
  const podeSalvar = pode('equipamento.verificar')
    && codigo.trim() !== '' && categoria !== ''
    && procedimento.trim().length >= 3
    && preenchidas.length + novasPreenchidas.length > 0
    && preenchidas.length <= LIMITE_DA_FOLHA
    && novasPreenchidas.length <= LIMITE_DA_FOLHA
    && casasDemais.length === 0
    && semInstrumento.length === 0
    && finasDemais.length === 0
    && novasIncompletas.length === 0
    && (novasPreenchidas.length === 0 || podeCriar)
    && !salvando;

  /* O próximo código da série, contando as linhas novas já abertas. */
  const proximoCodigoNovo = (jaAbertas: number): string => {
    const s = folha?.sugestaoCodigo;
    if (!s) return '';
    const n = Number(s.proximo) + jaAbertas;
    return `${s.prefixo}${String(n).padStart(s.largura, '0')}`;
  };

  const nomeDaCategoria = folha?.categorias
    .find((c) => c.categoria === categoria)?.categoria_nome ?? categoria;

  /*
   * O nome da linha nova segue o dos irmãos: se a «Cápsula 118» tem o
   * 118 do código no nome, a nova é a «Cápsula 119». Sem esse padrão, o
   * nome da categoria com o código — e a pessoa corrige na própria linha.
   */
  const nomeParaNovo = (cod: string): string => {
    const numNovo = cod.match(/(\d+)$/)?.[1];
    const modelo = [...(folha?.linhas ?? [])].reverse().find((l) => {
      const n = l.codigo_patrimonial.match(/(\d+)$/)?.[1];
      return n && l.nome.includes(n);
    });
    if (numNovo && modelo) {
      const n = modelo.codigo_patrimonial.match(/(\d+)$/)![1];
      const i = modelo.nome.lastIndexOf(n);
      return modelo.nome.slice(0, i) + numNovo + modelo.nome.slice(i + n.length);
    }
    return cod ? `${nomeDaCategoria} ${cod}` : '';
  };

  /*
   * A linha nova (onda 208), e desde a 210 com o prefixo da categoria.
   *
   * «ao adicionar nova linha mantenha o prefixo CAP- já pré escrito,
   *  assim o operador só precisa escreve o numero da capsula» — Humberto,
   * 24/09/2026. A cápsula tem o número GRAVADO: o sistema não inventa a
   * série, escreve o começo e a pessoa lê o resto no metal.
   */
  const abrirNovas = (quantas: number) => {
    const primeira = `nova-${seqNova.current + 1}`;
    if (prefixo) focoNoCodigo.current = primeira;
    setNovas((antes) => {
      const mais: Nova[] = [];
      for (let k = 0; k < quantas; k += 1) {
        seqNova.current += 1;
        const cod = prefixo || proximoCodigoNovo(antes.length + k);
        mais.push({
          chave: `nova-${seqNova.current}`,
          codigo: cod,
          /* Sem nome, o servidor dá «<categoria> <código>». */
          nome: prefixo || !pedeIdentificacao ? '' : nomeParaNovo(cod),
          abertura: '',
        });
      }
      return [...antes, ...mais];
    });
  };

  /* «99» numa categoria com prefixo é CAP-99. */
  const completarCodigo = (chave: string) => setNovas((l) => l.map((n) => {
    if (n.chave !== chave || !prefixo) return n;
    const c = n.codigo.trim();
    return /^\d+$/.test(c) ? { ...n, codigo: `${prefixo}${c}` } : n;
  }));

  const mudarNova = (chave: string, campo: keyof Nova, v: string) =>
    setNovas((l) => l.map((n) => (n.chave === chave ? { ...n, [campo]: v } : n)));
  const tirarNova = (chave: string) => {
    setNovas((l) => l.filter((n) => n.chave !== chave));
    setValores((x) => { const y = { ...x }; delete y[chave]; return y; });
  };

  const salvar = async () => {
    setSalvando(true);
    setErroSalvar(null);
    try {
      /* A peneira mede as duas colunas com o MESMO instrumento. */
      const padroes = Object.entries(instrumentoDa)
        .filter(([, v]) => v)
        .map(([grandeza, equipamentoId]) => ({ grandeza, equipamentoId }));
      const r = await api<{ id: string; codigo: string; linhas: number }>(
        '/verificacoes/lote', {
          corpo: {
            codigo: codigo.trim(),
            titulo: `Calibração interna — ${nomeDaCategoria}`,
            categoria,
            dataAfericao,
            procedimento: procedimento.trim(),
            balancaId: balancaId || undefined,
            padroes: padroes.length ? padroes : undefined,
            condicoesAmbientais: condicoes.trim() || undefined,
            proximaAfericao: proximaAfericao || undefined,
            observacoes: observacoes.trim() || undefined,
            linhas: preenchidas.map(({ l, meus }) => ({
              equipamentoId: l.equipamento_id,
              valores: meus,
              observacao: notas[l.equipamento_id]?.trim() || undefined,
            })),
            novos: novasPreenchidas.map(({ n, meus }) => ({
              codigoPatrimonial: n.codigo.trim(),
              nome: pedeIdentificacao && n.nome.trim() ? n.nome.trim() : undefined,
              aberturaNominalMm: ePeneira ? paraNumero(n.abertura) : undefined,
              valores: meus,
              observacao: notas[n.chave]?.trim() || undefined,
            })),
          },
        });
      navegar(`/equipamentos/verificacoes/${r.id}`);
    } catch (e) {
      setErroSalvar(e);
      setSalvando(false);
    }
  };

  /*
   * A baixa na própria linha (onda 208).
   *
   * Vai na hora, e não junto com a campanha: dar baixa não é medida, é
   * cadastro — e a cápsula extraviada não deve esperar alguém gravar a
   * folha para deixar de ser oferecida num ensaio. A linha some da folha;
   * o item continua no banco, com tipo, motivo, quem e quando.
   */
  const confirmarBaixa = async () => {
    if (!baixando) return;
    setEnviandoBaixa(true);
    setErroBaixa(null);
    try {
      await api(`/equipamentos/${baixando.equipamento_id}/baixa`, {
        corpo: { tipo: tipoBaixa, motivo: motivoBaixa.trim() },
      });
      const id = baixando.equipamento_id;
      setBaixados((b) => [...b, { codigo: baixando.codigo_patrimonial, tipo: tipoBaixa }]);
      setFolha((f) => (f ? { ...f, linhas: f.linhas.filter((l) => l.equipamento_id !== id) } : f));
      setValores((x) => { const y = { ...x }; delete y[id]; return y; });
      setBaixando(null);
      setMotivoBaixa('');
    } catch (e) {
      setErroBaixa(e);
    } finally {
      setEnviandoBaixa(false);
    }
  };

  /* A ordem em que o Enter desce: as linhas que existem, depois as novas. */
  const ordemDasLinhas = [
    ...linhasOrdenadas.map((l) => l.equipamento_id),
    ...novas.map((n) => n.chave),
  ];

  /*
   * Enter desce uma linha em vez de enviar o formulário.
   *
   * Quem pesa quarenta cápsulas tem uma mão na balança e a outra no
   * teclado; tirar a mão para o mouse quarenta vezes é o que faz a folha
   * de papel continuar ganhando da tela. Na última linha, o Enter abre
   * uma linha nova — é o gesto de quem tem mais uma cápsula na mão.
   */
  const aoTeclar = (e: React.KeyboardEvent, id: string, g: string) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const i = ordemDasLinhas.indexOf(id);
    let seguinte = ordemDasLinhas[i + 1];
    while (seguinte && campos.current[`${seguinte}|${g}`]?.disabled) {
      seguinte = ordemDasLinhas[ordemDasLinhas.indexOf(seguinte) + 1];
    }
    if (seguinte) {
      campos.current[`${seguinte}|${g}`]?.focus();
    } else if (podeCriar && pode('equipamento.verificar')) {
      focoNaNova.current = g;
      abrirNovas(1);
    }
  };

  /* Enter no código da linha nova vai para a primeira medida dela. */
  const aoTeclarCodigo = (e: React.KeyboardEvent, chave: string) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    completarCodigo(chave);
    const g = medidas[0]?.grandeza;
    if (g) campos.current[`${chave}|${g}`]?.focus();
  };

  /*
   * O que a geometria dá — para CONFERIR, não para gravar. A conta é a
   * do banco (`contaDaDerivada.ts`): π·d²/4·h no anel redondo, L₁·L₂ e
   * L₁·L₂·h no anel quadrado de cisalhamento (RN-224).
   */
  const derivado = (id: string, g: Grandeza): number | null =>
    contaDaDerivada(g, (x) => paraNumero(digitado(id, x)));

  if (erro && !folha) return <ErroDaApi erro={erro} />;
  if (!folha) return <Carregando texto="Carregando o parque" />;

  const naoPode = !pode('equipamento.verificar');
  const instrumentosDeDimensao = medidas.filter((g) => g.grandeza !== 'massa'
    && !DA_PENEIRA.includes(g.grandeza)
    && !SEGUE_O_INSTRUMENTO_DE[g.grandeza]);
  /* As grandezas que seguem o instrumento desta (L₂ segue L₁). */
  const quemSegue = (g: string) => Object.entries(SEGUE_O_INSTRUMENTO_DE)
    .filter(([, lider]) => lider === g).map(([seguidor]) => seguidor);

  /** As células de medida de uma linha — a mesma para a antiga e a nova. */
  const celulasDeMedida = (id: string, rotulo: string, bloqueada: boolean, nova?: Nova) => {
    const norma = ePeneira ? normaDaPeneira(id, nova) : null;
    return medidas.map((g) => {
      const texto = digitado(id, g.grandeza);
      const v = paraNumero(texto);
      const lim = casasDa_(g);
      const casaDemais = v !== undefined && lim !== null
        && Number(v.toFixed(lim)) !== v;
      /* A peneira já mostra, ao lado do número, se a norma o aceita. */
      let fora = false;
      if (norma && v !== undefined) {
        if (g.grandeza === 'abertura_media') fora = Math.abs(v - norma.w) > norma.y + 1e-9;
        if (g.grandeza === 'abertura_maxima') fora = v > norma.w + norma.x + 1e-9;
      }
      return (
        <td className="num" key={`n-${g.grandeza}`}>
          <input
            ref={(el) => { campos.current[`${id}|${g.grandeza}`] = el; }}
            className={`entrada-massa${casaDemais || fora ? ' invalido' : ''}`}
            type="text"
            inputMode="decimal"
            disabled={naoPode || bloqueada}
            aria-label={`${g.rotulo} de ${rotulo}`}
            aria-invalid={casaDemais || fora || undefined}
            title={fora ? 'Fora do critério da NBR NM ISO 3310-1' : undefined}
            value={texto}
            onKeyDown={(e) => aoTeclar(e, id, g.grandeza)}
            onChange={(e) => mudar(id, g.grandeza, e.currentTarget.value)}
          />
        </td>
      );
    });
  };

  /** A coluna do critério da peneira: w, w ± Y, até w + X. */
  const celulaDaNorma = (id: string, nova?: Nova) => {
    const n = normaDaPeneira(id, nova);
    if (!n) {
      return (
        <td className="pn-norma">
          <span className="sub">
            {nova ? 'informe a abertura' : 'sem abertura nominal no cadastro'}
          </span>
        </td>
      );
    }
    const trava = aferivel(n.w, resolucaoPeneira) === false;
    return (
      <td className="pn-norma">
        <span className="mono">
          {`${numero(n.w - n.y, 3)} a ${numero(n.w + n.y, 3)}`}
        </span>
        <span className="sub">{` · máx. ${numero(n.w + n.x, 3)}`}</span>
        {n.medir && <span className="sub pn-medir">{`medir ${n.medir}`}</span>}
        {trava && (
          <Selo tom="atencao" titulo={'A NBR NM ISO 3310-1 (5.2) pede leitura de '
            + `1 µm ou Y/4 (${numero(n.y / 4, 4)} mm), o que for maior.`}
          >
            Fina demais — calibração externa
          </Selo>
        )}
      </td>
    );
  };

  return (
    <div className="pagina">
      <div className="pagina-cabeca">
        <h1>Calibração interna em lote</h1>
        <p className="sub">
          A folha da campanha: uma linha por objeto da categoria, com o que
          vale hoje ao lado. A cápsula tem uma coluna; o anel de adensamento,
          que é redondo, tem diâmetro, altura, massa e o volume que sai dos
          dois primeiros; o anel de cisalhamento, que é quadrado, tem as duas
          larguras, a altura e a massa — a área e o volume saem delas; a
          peneira tem a abertura média e a maior abertura. Ponha um de cada vez e desça
          a lista — <b>Enter</b> pula para a linha seguinte. Item danificado
          ou extraviado sai pelo botão <b>Baixa</b> da linha; item novo entra
          por <b>+ Nova linha</b>. No fim, tudo vira um certificado só, com
          uma assinatura só.
        </p>
      </div>

      {naoPode && (
        <Aviso tipo="atencao" titulo="Você pode ver, e não gravar">
          Registrar calibração interna exige a permissão
          <b> equipamento.verificar</b>.
        </Aviso>
      )}

      {/* ── O cabeçalho da campanha ─────────────────────────────────── */}
      <div className="cartao">
        <h2>A campanha</h2>
        <div className="linha">
          <label className="campo">
            <span className="rotulo">O que vai ser calibrado</span>
            <select
              className="filtro-suspenso"
              aria-label="Categoria a calibrar"
              value={categoria}
              onChange={(e) => escolherCategoria(e.target.value)}
            >
              <option value="">Escolha a categoria…</option>
              {folha.categorias.map((c) => (
                <option key={c.categoria} value={c.categoria}>
                  {c.categoria_nome}
                  {' '}
                  ({c.itens}
                  {c.sem_massa > 0 && c.categoria !== 'peneira'
                    ? `, ${c.sem_massa} sem tara` : ''})
                </option>
              ))}
            </select>
          </label>

          {!ePeneira && (
            <label className="campo">
              <span className="rotulo">Balança usada</span>
              <select
                className="filtro-suspenso"
                aria-label="Balança da campanha"
                value={balancaId}
                onChange={(e) => setBalancaId(e.target.value)}
              >
                <option value="">Escolha a balança…</option>
                {folha.balancas.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.codigo_patrimonial}
                    {' — '}
                    {b.nome}
                    {b.resolucao !== null
                      ? ` (${numero(b.resolucao, 4, true)} ${b.resolucao_unidade ?? 'g'})`
                      : ''}
                  </option>
                ))}
              </select>
            </label>
          )}

          {/*
            * O certificado da balança vem sozinho — o último cadastrado
            * na ficha dela (onda 210, RN-201). Não se digita: um número
            * digitado aqui seria um número que a ficha não conhece.
            */}
          {!ePeneira && (
            <div className="campo fl-cert-padrao">
              <span className="rotulo">Certificado da balança</span>
              <span
                className={`fl-cert-valor${certBalanca && !certBalanca.cobre ? ' vencido' : ''}`}
                aria-label="Certificado da balança"
              >
                {!certBalanca && <span className="sub">escolha a balança</span>}
                {certBalanca && !certBalanca.numero && (
                  <span className="sub">nenhum cadastrado</span>
                )}
                {certBalanca?.numero && (
                  <>
                    <b className="mono">{certBalanca.numero}</b>
                    {certBalanca.ate && (
                      <span className="sub">
                        {` · ${certBalanca.cobre ? 'válido até' : 'venceu em'} ${dataBr(certBalanca.ate)}`}
                      </span>
                    )}
                  </>
                )}
              </span>
              <span className="dica">O último cadastrado na ficha da balança.</span>
            </div>
          )}

          <Campo
            rotulo="Nº deste certificado interno"
            value={codigo}
            onChange={(e) => setCodigo(e.currentTarget.value)}
            dica={folha.proximoCodigo
              ? `O que esta campanha emite — o seguinte na série é ${folha.proximoCodigo}.`
              : 'O que esta campanha emite. A numeração é a da casa.'}
          />

          <Campo
            rotulo="Data da calibração interna"
            type="date"
            value={dataAfericao}
            onChange={(e) => setDataAfericao(e.currentTarget.value)}
          />
        </div>

        {/*
          * Com que instrumento cada dimensão foi medida.
          *
          * A 17025 pede que o certificado diga com o que se mediu, e o
          * diâmetro de um anel não sai da balança. Sem este campo, o
          * certificado sairia afirmando que quatro grandezas foram
          * medidas numa balança.
          */}
        {instrumentosDeDimensao.length > 0 && (
          <div className="linha">
            {instrumentosDeDimensao.map((g) => (
              <label className="campo" key={g.grandeza}>
                <span className="rotulo">
                  {`Instrumento — ${ROTULO_DO_INSTRUMENTO[g.grandeza] ?? g.rotulo}`}
                </span>
                <select
                  className="filtro-suspenso"
                  aria-label={`Instrumento usado para ${ROTULO_DO_INSTRUMENTO[g.grandeza] ?? g.rotulo}`}
                  value={instrumentoDa[g.grandeza] ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setInstrumentoDa((m) => {
                      const n = { ...m, [g.grandeza]: v };
                      for (const s of quemSegue(g.grandeza)) n[s] = v;
                      return n;
                    });
                  }}
                >
                  <option value="">Escolha o instrumento…</option>
                  {folha.instrumentos.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.codigo_patrimonial}
                      {' — '}
                      {i.nome}
                      {i.resolucao !== null
                        ? ` (${numero(i.resolucao, 4, true)} ${i.resolucao_unidade ?? 'mm'})`
                        : ''}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        )}

        {/*
          * A peneira: UM instrumento para as duas colunas, e a fronteira
          * da norma dita antes de medir (RN-197).
          */}
        {ePeneira && (
          <div className="linha">
            <label className="campo">
              <span className="rotulo">Instrumento — Abertura</span>
              <select
                className="filtro-suspenso"
                aria-label="Instrumento usado para a abertura"
                value={instrumentoDa.abertura_media ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setInstrumentoDa((m) => ({ ...m, abertura_media: v, abertura_maxima: v }));
                }}
              >
                <option value="">Escolha o instrumento…</option>
                {folha.instrumentos.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.codigo_patrimonial}
                    {' — '}
                    {i.nome}
                    {i.resolucao !== null
                      ? ` (${numero(i.resolucao, 4, true)} ${i.resolucao_unidade ?? 'mm'})`
                      : ''}
                  </option>
                ))}
              </select>
            </label>
            <div className="campo pn-fronteira">
              <span className="rotulo">Até que abertura a casa afere</span>
              {!instrumentoPeneira && (
                <span className="sub">Escolha o instrumento para ver a fronteira.</span>
              )}
              {instrumentoPeneira && resolucaoPeneira === null && (
                <span className="sub">
                  O instrumento não tem resolução cadastrada em mm — sem ela a
                  norma não diz até onde ele alcança.
                </span>
              )}
              {instrumentoPeneira && resolucaoPeneira !== null && (
                <span>
                  <b>
                    {num(instrumentoPeneira.menor_peneira_mm) !== null
                      ? `a partir de ${numero(num(instrumentoPeneira.menor_peneira_mm), 3, true)} mm`
                      : 'nenhuma abertura da norma'}
                  </b>
                  <span className="sub">
                    {` com resolução de ${numero(resolucaoPeneira, 4, true)} mm. `}
                    A NBR NM ISO 3310-1 (5.2) pede leitura de 1 µm ou ¼ da
                    tolerância Y, o que for maior; abaixo disso, microscópio
                    — calibração externa
                  </span>
                  {' '}
                  <Rn codigo="RN-197"
                    diz="A peneira só é calibrada internamente se a resolução do instrumento for ≤ max(1 µm; Y/4) — NBR NM ISO 3310-1, 5.2." />
                </span>
              )}
            </div>
          </div>
        )}

        <div className="linha">
          <Campo
            rotulo="Procedimento ou norma"
            value={procedimento}
            onChange={(e) => setProcedimento(e.currentTarget.value)}
            dica={ePeneira
              ? 'Obrigatório — ex.: NBR NM ISO 3310-1:2010, 5.2 (ensaios 1 a 3). Vai impresso no certificado.'
              : 'Obrigatório — o que manda calibrar assim. Vai impresso no certificado.'}
          />
          <Campo
            rotulo="Condições ambientais"
            value={condicoes}
            onChange={(e) => setCondicoes(e.currentTarget.value)}
            dica="Opcional — ex.: 22 °C, 60 % UR."
          />
          <Campo
            rotulo="Próxima calibração interna"
            type="date"
            value={proximaAfericao}
            onChange={(e) => setProximaAfericao(e.currentTarget.value)}
            dica="Opcional."
          />
        </div>

        {balanca && !ePeneira && (
          <div className="faixa-resultado" style={{ marginTop: 8 }}>
            <p className="sub" style={{ margin: 0 }}>
              {balanca.casas_decimais !== null
              && balanca.casas_decimais !== undefined
                ? (
                  <>
                    Esta balança lê
                    {' '}
                    <b>
                      {plural(balanca.casas_decimais, 'casa decimal', 'casas decimais')}
                    </b>
                    {' '}
                    em grama. Um dígito a mais não existe no instrumento, e o
                    banco recusa a leitura.
                  </>
                )
                : (
                  <>
                    Esta balança não tem resolução cadastrada em unidade
                    conhecida, então a folha não limita as casas decimais.
                  </>
                )}
            </p>
            {certBalanca && !certBalanca.cobre && (
              <Aviso tipo="atencao" titulo="O certificado da balança não cobre este dia">
                {certBalanca.numero
                  ? `O último certificado cadastrado (${certBalanca.numero}) ${
                    certBalanca.ate ? `vale até ${dataBr(certBalanca.ate)}` : 'não tem validade'}. `
                  : 'A ficha da balança não tem certificado cadastrado. '}
                A campanha grava normalmente; o certificado interno fica
                {' '}
                <b>aguardando o certificado da balança</b>
                {` que cubra ${dataBr(dataAfericao)}. Cadastre-o na ficha da balança e ele fica apto para aprovação.`}
                {' '}
                <Rn codigo="RN-201"
                  diz="A calibração interna grava com o padrão vencido na plataforma, mas só é aprovada quando existir, para cada padrão, calibração externa não reprovada que cubra a data da calibração interna." />
              </Aviso>
            )}
            {balanca.impedimento && certBalanca?.cobre && (
              <Aviso tipo="atencao" titulo="Esta balança tem pendência">
                {balanca.impedimento_curto}
                {' '}
                — a campanha fica registrada com essa balança como padrão,
                e quem ler o certificado vai ver isso. Confira antes de pesar.
              </Aviso>
            )}
          </div>
        )}
      </div>

      {/* ── A folha ─────────────────────────────────────────────────── */}
      {categoria === '' && (
        <div className="cartao">
          <Vazio>
            Escolha a categoria acima e a folha aparece aqui, com uma linha
            por objeto.
          </Vazio>
        </div>
      )}

      {categoria !== '' && carregandoLinhas && (
        <Carregando texto="Montando a folha" />
      )}

      {baixados.length > 0 && (
        <Aviso tipo="ok" titulo="Baixa registrada">
          {baixados.map((b) => `${b.codigo} (${rotulo(b.tipo)})`).join(', ')}
          {' — '}
          {baixados.length === 1 ? 'saiu' : 'saíram'}
          {' do parque e da folha. O registro continua, com o motivo, para o '}
          laudo que já o usou.
        </Aviso>
      )}

      {categoria !== '' && !carregandoLinhas && (
        <div className="cartao">
          <div className="linha-topo">
            <h2 className="cresce">
              A folha
              {' — '}
              {plural(folha.linhas.length + novas.length, 'objeto', 'objetos')}
            </h2>
            <Selo tom={preenchidas.length + novasPreenchidas.length > 0 ? 'ok' : 'neutro'}>
              {plural(preenchidas.length + novasPreenchidas.length, 'lançado', 'lançados')}
            </Selo>
          </div>

          {derivadas.length > 0 && (
            <p className="sub">
              {derivadas.map((g) => `${g.rotulo}: ${g.formula}`).join(' · ')}
              {' — a coluna calculada aparece para conferência e não é '}
              digitada. Quem a publica é o banco, na aprovação.
            </p>
          )}
          {ePeneira && (
            <p className="sub">
              Critério da NBR NM ISO 3310-1: a abertura média fica em
              {' '}
              <b>w ± Y</b>
              {' (5.1.1.3) e nenhuma abertura passa de '}
              <b>w + X</b>
              {' (5.1.1.2), com X e Y das Tabelas 1 e 2 (ou das fórmulas (1) e (2) '}
              para abertura fora delas). O veredito é do banco, ao gravar.
              O desvio-padrão σ₀ e o diâmetro do fio não entram nesta folha.
            </p>
          )}

          {folha.linhas.length === 0 && novas.length === 0 && (
            <Vazio>Não há nenhum objeto ativo nesta categoria. Use «+ Nova linha».</Vazio>
          )}

          {(folha.linhas.length > 0 || novas.length > 0) && (
            <div className="rola">
              <table className="dados folha-de-massa">
                <thead>
                  <tr>
                    {/*
                      * Ordenar pelo próprio cabeçalho (onda 210). O botão
                      * dentro do <th> e o aria-sort: o leitor de tela diz
                      * «Código, ordenado crescente».
                      */}
                    <th aria-sort={ordem.campo === 'codigo'
                      ? (ordem.sentido === 1 ? 'ascending' : 'descending') : 'none'}
                    >
                      <button type="button" className="fl-ordena" onClick={() => ordenarPor('codigo')}>
                        Código
                        <span aria-hidden="true" className="fl-seta">
                          {ordem.campo === 'codigo' ? (ordem.sentido === 1 ? '▲' : '▼') : '↕'}
                        </span>
                      </button>
                    </th>
                    {pedeIdentificacao && <th>Identificação</th>}
                    <th aria-sort={ordem.campo === 'situacao'
                      ? (ordem.sentido === 1 ? 'ascending' : 'descending') : 'none'}
                    >
                      <button type="button" className="fl-ordena" onClick={() => ordenarPor('situacao')}>
                        Situação
                        <span aria-hidden="true" className="fl-seta">
                          {ordem.campo === 'situacao' ? (ordem.sentido === 1 ? '▲' : '▼') : '↕'}
                        </span>
                      </button>
                    </th>
                    {ePeneira && <th>Critério (mm)</th>}
                    {!ePeneira && medidas.map((g) => (
                      <th className="num" key={`a-${g.grandeza}`}>
                        {`${g.rotulo} anterior (${g.unidade})`}
                      </th>
                    ))}
                    {medidas.map((g) => (
                      <th className="num" key={`n-${g.grandeza}`}>
                        {`${g.rotulo} (${g.unidade})`}
                      </th>
                    ))}
                    {soMassa && <th className="num">Δ (g)</th>}
                    {derivadas.map((g) => (
                      <th className="num" key={`d-${g.grandeza}`}>
                        {`${g.rotulo} (${g.unidade})`}
                      </th>
                    ))}
                    <th>Observação</th>
                    <th><span className="so-leitor">Ações da linha</span></th>
                  </tr>
                </thead>
                <tbody>
                  {linhasOrdenadas.map((l) => {
                    const id = l.equipamento_id;
                    const alguma = medidas.some((g) =>
                      paraNumero(digitado(id, g.grandeza)) !== undefined);
                    const trava = travadaPelaNorma(id);
                    return (
                      <tr
                        key={id}
                        className={alguma ? 'linha-pesada' : trava ? 'linha-apagada' : undefined}
                      >
                        <td className="mono" title={l.localizacao ?? undefined}>
                          {l.codigo_patrimonial}
                        </td>
                        {pedeIdentificacao && (
                          <td>
                            {l.nome}
                            {l.localizacao && (
                              <span className="sub">
                                {' · '}
                                {l.localizacao}
                              </span>
                            )}
                          </td>
                        )}
                        <td className="fl-situacao">
                          {l.impedimento
                            ? (
                              <Selo tom="atencao" titulo={l.impedimento_curto ?? undefined}>
                                {l.impedimento_curto ?? 'pendência'}
                              </Selo>
                            )
                            : !comValor.has(id)
                              ? <Selo tom="neutro">Sem valor</Selo>
                              : (
                              <>
                                <Selo tom="ok">Em dia</Selo>
                                {l.valido_ate && (
                                  <span className="sub">{` até ${dataBr(l.valido_ate)}`}</span>
                                )}
                              </>
                            )}
                        </td>

                        {ePeneira && celulaDaNorma(id)}

                        {!ePeneira && medidas.map((g) => {
                          const a = anteriorDe.get(`${id}|${g.grandeza}`);
                          const casasCol = casasDa_(g)
                            ?? CASAS_PADRAO[g.grandeza] ?? 2;
                          return (
                            <td className="num" key={`a-${g.grandeza}`}>
                              {!a || a.valor_anterior === null
                                ? <span className="sub">sem valor</span>
                                : (
                                  <span title={a.anterior_certificado
                                    ? `Certificado ${a.anterior_certificado}`
                                    : undefined}
                                  >
                                    {numero(Number(a.valor_anterior), casasCol)}
                                  </span>
                                )}
                            </td>
                          );
                        })}

                        {celulasDeMedida(id, l.codigo_patrimonial, trava)}

                        {soMassa && (() => {
                          const a = anteriorDe.get(`${id}|massa`);
                          const v = paraNumero(digitado(id, 'massa'));
                          const delta = v !== undefined && a?.valor_anterior != null
                            ? v - Number(a.valor_anterior) : null;
                          return (
                            <td className="num">
                              {delta === null
                                ? <span className="sub">—</span>
                                : (
                                  <span className="mono">
                                    {sinal(delta, casas ?? 2)}
                                  </span>
                                )}
                            </td>
                          );
                        })()}

                        {derivadas.map((g) => {
                          const v = derivado(id, g);
                          return (
                            <td className="num" key={`d-${g.grandeza}`}>
                              {v === null
                                ? <span className="sub">—</span>
                                : (
                                  <span className="mono" title={g.formula ?? undefined}>
                                    {numero(v, CASAS_DA_DERIVADA[g.grandeza] ?? CASAS_PADRAO[g.grandeza] ?? 3)}
                                  </span>
                                )}
                            </td>
                          );
                        })}

                        <td>
                          <input
                            className="entrada-nota"
                            type="text"
                            disabled={naoPode}
                            aria-label={`Observação de ${l.codigo_patrimonial}`}
                            value={notas[id] ?? ''}
                            onChange={(e) => setNotas((n) => ({
                              ...n, [id]: e.currentTarget.value,
                            }))}
                          />
                        </td>
                        <td className="fl-acoes">
                          {podeCriar && (
                            <button
                              type="button"
                              className="fl-baixa"
                              onClick={() => {
                                setBaixando(l); setTipoBaixa('inutilizado');
                                setMotivoBaixa(''); setErroBaixa(null);
                              }}
                            >
                              Baixa
                              <span className="so-leitor">{` de ${l.codigo_patrimonial}`}</span>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {novas.map((n) => {
                    const trava = travadaPelaNorma(n.chave, n);
                    return (
                      <tr key={n.chave} className="linha-nova">
                        <td>
                          <input
                            ref={(el) => { campos.current[`${n.chave}|codigo`] = el; }}
                            className="entrada-codigo mono"
                            type="text"
                            value={n.codigo}
                            aria-label="Código do item novo"
                            placeholder={prefixo ? `${prefixo}000` : undefined}
                            onKeyDown={(e) => aoTeclarCodigo(e, n.chave)}
                            onBlur={() => completarCodigo(n.chave)}
                            onChange={(e) => mudarNova(n.chave, 'codigo', e.currentTarget.value.toUpperCase())}
                          />
                          {!pedeIdentificacao && (
                            <span className="sub fl-novo">item novo — nasce ao gravar</span>
                          )}
                        </td>
                        {pedeIdentificacao && (
                        <td>
                          <input
                            className="entrada-nota"
                            type="text"
                            value={n.nome}
                            aria-label={`Identificação de ${n.codigo || 'item novo'}`}
                            onChange={(e) => mudarNova(n.chave, 'nome', e.currentTarget.value)}
                          />
                          {ePeneira && (
                            <label className="pn-abertura">
                              <span className="sub">abertura nominal (mm)</span>
                              <input
                                className="entrada-massa"
                                type="text"
                                inputMode="decimal"
                                value={n.abertura}
                                aria-label={`Abertura nominal de ${n.codigo || 'item novo'} (mm)`}
                                onChange={(e) => mudarNova(n.chave, 'abertura', e.currentTarget.value)}
                              />
                            </label>
                          )}
                          <span className="sub fl-novo">item novo — nasce ao gravar</span>
                        </td>
                        )}
                        <td className="fl-situacao"><Selo tom="neutro">Novo</Selo></td>
                        {ePeneira && celulaDaNorma(n.chave, n)}
                        {!ePeneira && medidas.map((g) => (
                          <td className="num" key={`a-${g.grandeza}`}>
                            <span className="sub">novo</span>
                          </td>
                        ))}
                        {celulasDeMedida(n.chave, n.codigo || 'item novo', trava, n)}
                        {soMassa && <td className="num"><span className="sub">—</span></td>}
                        {derivadas.map((g) => {
                          const v = derivado(n.chave, g);
                          return (
                            <td className="num" key={`d-${g.grandeza}`}>
                              {v === null ? <span className="sub">—</span>
                                : <span className="mono">{numero(v, CASAS_DA_DERIVADA[g.grandeza] ?? CASAS_PADRAO[g.grandeza] ?? 3)}</span>}
                            </td>
                          );
                        })}
                        <td>
                          <input
                            className="entrada-nota"
                            type="text"
                            aria-label={`Observação de ${n.codigo || 'item novo'}`}
                            value={notas[n.chave] ?? ''}
                            onChange={(e) => setNotas((x) => ({
                              ...x, [n.chave]: e.currentTarget.value,
                            }))}
                          />
                        </td>
                        <td className="fl-acoes">
                          <button
                            type="button"
                            className="fl-baixa"
                            onClick={() => tirarNova(n.chave)}
                          >
                            Tirar
                            <span className="so-leitor">{` a linha nova ${n.codigo}`}</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/*
            * As linhas novas (onda 208). Só quem cadastra equipamento as
            * abre: o item nasce no parque, e o parque tem dono.
            */}
          <div className="fl-rodape">
            {podeCriar && !naoPode ? (
              <>
                <button type="button" className="botao secundario" onClick={() => abrirNovas(1)}>
                  + Nova linha
                </button>
                <button type="button" className="pq-alternar" onClick={() => abrirNovas(5)}>
                  + 5 linhas
                </button>
                <span className="sub">
                  {prefixo
                    ? `A linha nova já vem com ${prefixo} — digite só o número gravado na peça e Enter.`
                    : folha.sugestaoCodigo
                      ? `O código segue a série (${proximoCodigoNovo(novas.length)} é o próximo); pode trocar.`
                      : 'Sem série reconhecível nos códigos desta categoria — digite o código.'}
                  {' '}
                  <Rn codigo="RN-198"
                    diz="A folha cria item e dá baixa: criar é cadastrar (equipamento.editar) e nasce na mesma transação da calibração interna; a baixa não apaga (RN-159)." />
                </span>
              </>
            ) : (
              <span className="sub">
                Incluir item novo e dar baixa exigem a permissão de cadastrar
                equipamento (<b>equipamento.editar</b>).
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── O fecho ─────────────────────────────────────────────────── */}
      {categoria !== '' && (folha.linhas.length > 0 || novas.length > 0) && (
        <div className="cartao">
          <h2>Fechar a campanha</h2>

          {casasDemais.length > 0 && (
            <Aviso tipo="erro" titulo="Casas decimais a mais">
              {balanca?.codigo_patrimonial ?? 'A balança'}
              {' lê '}
              {plural(casas ?? 0, 'casa', 'casas')}
              {'. Estas linhas têm mais: '}
              <b>{casasDemais.join(', ')}</b>
              . Confira a leitura ou a balança — o dígito a mais não existe
              no instrumento.
            </Aviso>
          )}

          {finasDemais.length > 0 && (
            <Aviso tipo="erro" titulo="Peneira fina demais para o instrumento">
              {'Estas peneiras têm medida lançada e o instrumento escolhido não '}
              {'as alcança: '}
              <b>{finasDemais.join(', ')}</b>
              . A NBR NM ISO 3310-1 (5.2) pede leitura de 1 µm ou ¼ de Y —
              elas vão para calibração externa (RN-197).
            </Aviso>
          )}

          {novasIncompletas.length > 0 && (
            <Aviso tipo="atencao" titulo="Linha nova incompleta">
              {'Falta código, identificação'}
              {ePeneira ? ' ou abertura nominal' : ''}
              {': '}
              <b>{novasIncompletas.join(', ')}</b>
              .
            </Aviso>
          )}

          {semInstrumento.length > 0 && (
            <Aviso tipo="atencao" titulo="Falta dizer com o que foi medido">
              {'Estas grandezas foram lançadas e não têm instrumento '}
              {'declarado: '}
              <b>
                {[...new Set(semInstrumento
                  .map((g) => (DA_PENEIRA.includes(g) ? 'Abertura'
                    : ROTULO_DO_INSTRUMENTO[SEGUE_O_INSTRUMENTO_DE[g] ?? g]
                      ?? colunas.find((c) => c.grandeza === g)?.rotulo ?? g)))]
                  .join(', ')}
              </b>
              . O certificado precisa dizer com o que se mediu.
            </Aviso>
          )}

          {maiorDesvio && (
            <p className="sub">
              O maior desvio deste lote é
              {' '}
              <b>
                {sinal(maiorDesvio.d,
                  CASAS_PADRAO[maiorDesvio.g.grandeza] ?? 2)}
              </b>
              {` ${maiorDesvio.g.unidade} em `}
              <b>{maiorDesvio.l.codigo_patrimonial}</b>
              {` (${maiorDesvio.g.rotulo}). `}
              O sistema não julga esse número — não há tolerância cadastrada
              para objeto calibrado. Quem olha é você.
            </p>
          )}

          <Campo
            rotulo="Observações do certificado"
            value={observacoes}
            onChange={(e) => setObservacoes(e.currentTarget.value)}
            dica="Opcional — vai impresso."
          />

          {faltaParaGravar.length > 0 && (
            <Aviso tipo="atencao" titulo="Falta para gravar">
              <span data-falta-para-gravar>
                {faltaParaGravar.length === 1
                  ? `Falta ${faltaParaGravar[0]}.`
                  : (
                    <>
                      Faltam:
                      <ul style={{ margin: '4px 0 0' }}>
                        {faltaParaGravar.map((f) => <li key={f}>{f}</li>)}
                      </ul>
                    </>
                  )}
              </span>
            </Aviso>
          )}

          {erroSalvar !== null && <ErroDaApi erro={erroSalvar} />}

          <div className="linha-topo" style={{ marginTop: 12 }}>
            <p className="sub cresce" style={{ margin: 0 }}>
              {preenchidas.length + novasPreenchidas.length === 0
                ? 'Nenhum valor digitado ainda.'
                : (
                  <>
                    Vai gravar
                    {' '}
                    <b>{plural(preenchidas.length + novasPreenchidas.length, 'linha', 'linhas')}</b>
                    {novasPreenchidas.length > 0
                      ? ` (${plural(novasPreenchidas.length, 'item novo', 'itens novos')}, cadastrados na mesma gravação)`
                      : ''}
                    {' em um certificado em rascunho. Publicar é aprovar, '}
                    e aprovar é de outra pessoa (§15).
                  </>
                )}
            </p>
            <button
              type="button"
              className="botao"
              disabled={!podeSalvar}
              onClick={salvar}
            >
              {salvando ? 'Gravando…' : 'Gravar a campanha'}
            </button>
          </div>
        </div>
      )}

      {/* ── A baixa ─────────────────────────────────────────────────── */}
      <Dialogo
        aberto={baixando !== null}
        titulo={baixando ? `Dar baixa em ${baixando.codigo_patrimonial}` : 'Dar baixa'}
        aoFechar={() => setBaixando(null)}
      >
        {baixando && (
          <div className="fl-dialogo">
            <p className="sub">
              {baixando.nome}
              {'. O item sai do parque e da folha, e não é mais oferecido em '}
              ensaio. O registro NÃO é apagado: o laudo que já usou a tara
              dele continua dizendo qual era.
              {' '}
              <Rn codigo="RN-159" diz="Baixa não apaga: registra tipo, motivo, quem e quando." />
            </p>
            <label className="campo">
              <span className="rotulo">O que aconteceu</span>
              <select
                className="filtro-suspenso"
                aria-label="Tipo de baixa"
                value={tipoBaixa}
                onChange={(e) => setTipoBaixa(e.currentTarget.value)}
              >
                {TIPOS_DE_BAIXA.map((t) => (
                  <option key={t.codigo} value={t.codigo}>{t.nome}</option>
                ))}
              </select>
            </label>
            <label className="campo">
              <span className="rotulo">Motivo</span>
              <textarea
                rows={3}
                aria-label="Motivo da baixa"
                value={motivoBaixa}
                placeholder="Ex.: cápsula amassada na estufa em 22/09; tara deixou de ser confiável."
                onChange={(e) => setMotivoBaixa(e.currentTarget.value)}
              />
              <span className="dica">Pelo menos 10 caracteres — o auditor vai ler isto.</span>
            </label>
            {erroBaixa !== null && <ErroDaApi erro={erroBaixa} />}
            <div className="linha-topo">
              <span className="cresce" />
              <button type="button" className="botao discreto" onClick={() => setBaixando(null)}>
                Cancelar
              </button>
              <button
                type="button"
                className="botao perigo"
                disabled={motivoBaixa.trim().length < 10 || enviandoBaixa}
                onClick={confirmarBaixa}
              >
                {enviandoBaixa ? 'Registrando…' : 'Dar baixa'}
              </button>
            </div>
          </div>
        )}
      </Dialogo>
    </div>
  );
}
